import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Repeat, Clock, Loader2 } from "lucide-react";
import { haptic, HAPTIC } from "./motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MobileEstimateWidget — a compact card on the mobile home that shows a live
 * price estimate as the user picks trip type / vehicle / hours. "Book this ride"
 * hands the pre-filled state to `/m/chauffeur` via router state.
 */
export default function MobileEstimateWidget() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tripType, setTripType] = useState("hourly");
  const [vehicleId, setVehicleId] = useState("");
  const [hours, setHours] = useState(3);
  const [estimate, setEstimate] = useState(null);
  const [estLoading, setEstLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/vehicles`, { params: { available_for: "chauffeur" } });
        if (cancelled) return;
        setVehicles(data || []);
        if (data?.length) setVehicleId(data[0].id);
      } catch (_) { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const selected = useMemo(() => vehicles.find(v => v.id === vehicleId) || null, [vehicles, vehicleId]);
  const vehicleMinH = Math.max(selected?.min_hours || 3, 1);
  const currentMin = tripType === "one_way" ? 1 : vehicleMinH;

  // When trip type changes, snap the hours to that type's typical default.
  useEffect(() => {
    if (tripType === "one_way") {
      setHours(1.5);
    } else {
      // hourly / round_trip both enforce vehicle min-hours (default 3)
      setHours(vehicleMinH);
    }
  }, [tripType, vehicleMinH]);

  // Debounced estimate fetch
  useEffect(() => {
    if (!vehicleId || !selected) { setEstimate(null); return; }
    const h = setTimeout(async () => {
      setEstLoading(true);
      try {
        const { data } = await axios.post(`${API}/estimates/ride`, {
          vehicle_id: vehicleId,
          pickup: "-",
          dropoff: "-",
          estimated_duration_hours: Number(hours) || currentMin,
          trip_type: tripType,
        });
        setEstimate(data);
      } catch (_) { setEstimate(null); }
      finally { setEstLoading(false); }
    }, 220);
    return () => clearTimeout(h);
  }, [vehicleId, hours, tripType, selected, currentMin]);

  const bookNow = () => {
    haptic(HAPTIC.select);
    navigate("/m/chauffeur", {
      state: {
        prefill: {
          vehicle_id: vehicleId,
          trip_type: tripType,
          estimated_duration_hours: Number(hours) || currentMin,
        },
      },
    });
  };

  const total = estimate?.total ?? 0;
  const deposit = total ? total * 0.5 : 0;

  return (
    <motion.section
      data-testid="mobile-estimate-widget"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="mx-5 rounded-3xl overflow-hidden relative"
      style={{
        background: "linear-gradient(160deg, rgba(57,214,255,0.12) 0%, rgba(0,0,0,0.6) 65%)",
        border: "1px solid rgba(57,214,255,0.4)",
        boxShadow: "0 20px 45px -20px rgba(57,214,255,0.35), inset 0 1px 0 rgba(248,250,252,0.15)",
      }}
    >
      <div className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.35em] text-[#39D6FF]">
            <Sparkles size={11} /> INSTANT ESTIMATE
          </span>
        </div>

        {/* Trip type mini-toggle */}
        <div className="grid grid-cols-3 gap-1.5 mb-3">
          {[
            { k: "one_way",    label: "One-way",    Icon: ArrowRight },
            { k: "round_trip", label: "Round-trip", Icon: Repeat },
            { k: "hourly",     label: "Hourly",     Icon: Clock },
          ].map(({ k, label, Icon }) => {
            const active = tripType === k;
            return (
              <button
                key={k}
                type="button"
                data-testid={`est-tt-${k}`}
                onClick={() => { haptic(HAPTIC.tap); setTripType(k); }}
                className={`py-1.5 rounded-full inline-flex items-center justify-center gap-1 text-[10px] tracking-[0.22em] transition ${
                  active
                    ? "bg-[#39D6FF] text-black font-semibold"
                    : "border border-white/10 bg-white/5 text-white/70 hover:text-white"
                }`}
              >
                <Icon size={11} />
                {label.toUpperCase()}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-2 mb-3">
          <select
            className="w-full bg-white/5 border border-white/10 text-white text-xs rounded-xl px-3 py-2 outline-none focus:border-[#39D6FF]/60"
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            data-testid="est-vehicle"
            disabled={loading}
          >
            {loading ? (
              <option>Loading…</option>
            ) : vehicles.length === 0 ? (
              <option>No vehicles</option>
            ) : (
              vehicles.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))
            )}
          </select>
          <input
            type="number"
            min={currentMin}
            step="0.5"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            data-testid="est-hours"
            className="w-full bg-white/5 border border-white/10 text-white text-xs rounded-xl px-3 py-2 outline-none focus:border-[#39D6FF]/60"
            placeholder="Hours"
          />
        </div>

        {/* Price display */}
        <div className="rounded-2xl border border-[#39D6FF]/25 bg-[#071A2F]/40 p-4">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-[9px] tracking-[0.35em] text-[#39D6FF]/70">ESTIMATED TOTAL</div>
              <div className="text-[10px] text-white/40 mt-0.5">
                {tripType === "hourly" ? "Hourly service" : tripType === "one_way" ? "One-way / airport drop" : "Round-trip"}
                {selected ? ` · ${selected.name}` : ""}
              </div>
            </div>
            <div className="text-right">
              {estLoading ? (
                <Loader2 size={20} className="animate-spin text-[#39D6FF] inline" />
              ) : (
                <div data-testid="est-total" className="font-serif text-3xl text-transparent bg-clip-text bg-gradient-to-b from-[#F8FAFC] to-[#39D6FF]">
                  ${total.toFixed(0)}
                </div>
              )}
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] pt-2 border-t border-[#39D6FF]/15">
            <span className="text-white/50">Deposit to reserve (50%)</span>
            <span data-testid="est-deposit" className="text-[#39D6FF] font-medium">${deposit.toFixed(2)}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={bookNow}
          data-testid="est-book"
          disabled={!vehicleId}
          className="mt-4 w-full py-3 rounded-full text-[11px] tracking-[0.3em] font-bold text-black active:scale-[0.98] transition disabled:opacity-50"
          style={{
            background: "linear-gradient(180deg, #F8FAFC 0%, #39D6FF 50%, #39D6FF 100%)",
            boxShadow: "0 6px 22px -6px rgba(57,214,255,0.6), inset 0 1px 0 rgba(255,255,255,0.55)",
          }}
        >
          BOOK THIS RIDE
        </button>
      </div>
    </motion.section>
  );
}
